import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-start gap-4 p-4 rounded-lg hover:bg-background-100 transition-colors duration-200"
      >
        <div className="flex-shrink-0 w-20 h-20 rounded-md overflow-hidden">
          <img
            src={article.image}
            alt={article.imageAlt}
            className="w-full h-full object-cover"
            title={article.title}
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
            {article.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-1">
            {article.date} · {article.readTime}
          </p>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
      >
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={article.image}
            alt={article.imageAlt}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            title={article.title}
          />
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            {article.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-primary-100/70 text-primary-600">
                {tag}
              </span>
            ))}
          </div>
          <h3 className="text-lg font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2" style={{ fontFamily: 'var(--font-heading)' }}>
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 mt-2 line-clamp-2">{article.excerpt}</p>
          <div className="flex items-center gap-3 mt-4 text-xs text-foreground-500">
            <span>{article.author}</span>
            <span>·</span>
            <span>{article.date}</span>
            <span>·</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group flex flex-col sm:flex-row gap-5 p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
    >
      <div className="flex-shrink-0 w-full sm:w-48 h-36 rounded-lg overflow-hidden">
        <img
          src={article.image}
          alt={article.imageAlt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          title={article.title}
        />
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {article.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-secondary-100/80 text-secondary-700">
                {tag}
              </span>
            ))}
          </div>
          <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2" style={{ fontFamily: 'var(--font-heading)' }}>
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 mt-2 line-clamp-2">{article.excerpt}</p>
        </div>
        <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
          <span>{article.author}</span>
          <span>·</span>
          <span>{article.date}</span>
          <span>·</span>
          <span>{article.readTime}</span>
        </div>
      </div>
    </Link>
  );
}